// EVERY STRING THE FIRST-RUN SURFACE WRITES — T-177; REQ-005, `docs/design/mockups/web/w11-first-run.html`.
//
// The CARDS' sentences are never here: they are the server's (`FirstRun.cards[].message`), and so are the
// last card's two forms (`first_run.alerts.enable` / `.enable.plan` / `.already_watching`) and the thin
// variant's heading (`first_run.not_enough_history`). What this file holds is the frame around them — the
// title, the eyebrow per card key, the way-on buttons, and the words w11 writes beside the numbers.
//
// One file, so T-182 can lift it to the phone and hold it to this one as TEXT (the rule T-178 / T-180 /
// T-181 followed for `copy.ts`, `AlertsScreen.copy.ts` and `HomeScreen.briefing.copy.ts`).

/**
 * `?firstRun=1` on Home — how the Help entry re-opens the cards (REQ-005 AC1). It is state in the ADDRESS
 * rather than in a provider for the same two reasons `?rule=` is (D-430): a QA switch can reach the surface
 * without a click, and a reader who reloads is still looking at what they opened. It is NOT a QA switch
 * (`app/config.ts`): nothing in it reaches the demo server.
 */
export const FIRST_RUN_PARAM = 'firstRun';

/**
 * Where the Help entry goes. The period parameters are deliberately NOT carried: `PeriodProvider` fills an
 * absent range from the saved preferences, which it rewrites whenever the range changes — so the date
 * control opens on the range this reader last chose, without this link having to know its four names.
 */
export const FIRST_RUN_HREF = `/home?${FIRST_RUN_PARAM}=1`;

export const FIRST_RUN_COPY = {
  /** The surface's accessible name, and the document's while it is open. */
  label: 'Your first look',
  overline: 'Your first look',
  title: "Here's what's happening with your money",
  lead: 'Five things worth knowing before you look around. No setup, no tour — just your numbers, in plain words.',

  /** The progress indicator: "3 of 5", with the long form as its accessible name. */
  progress: (at: number, of: number): string => `${at} of ${of}`,
  progressLabel: (at: number, of: number): string => `Card ${at} of ${of}`,
  /** The list beside the card: what this covers, with the one you are on marked. */
  stepsLabel: 'What this covers',
  stepCurrent: 'On this one',
  stepDone: 'Seen',

  next: 'Next',
  /** The accessible name of Next, so the button says where it goes without shouting it. */
  nextTo: (label: string): string => `Next: ${label}`,
  back: 'Back',
  /** REQ-005 AC1 — skippable at every step. Escape does the same thing (D — see `FirstRun.tsx`). */
  skip: 'Skip to Home',
  skipping: 'Closing…',
  reopen: 'You can reopen this any time from Help.',

  /** The eyebrow of each card the server can send (ENGINE §12's five keys). A key this build does not know
   *  gets no eyebrow rather than an invented one — the sentence is what matters and it is the server's. */
  cardLabel: {
    spent: 'What you spent',
    results: 'What you got for it',
    best_performer: 'Your best value',
    concern: 'Your biggest concern',
    pacing: 'How this month is pacing',
  } as Readonly<Record<string, string>>,

  /** Where a card's `AppLink` goes, said in the words w11 uses. */
  open: {
    spendPacing: 'See it in Spend & Budgets',
    spendAllocation: 'See where your money goes',
    campaigns: 'See which campaigns',
    campaign: 'Open this campaign',
    adset: 'Open this ad set',
    ad: 'Open this ad',
    entity: 'Open it',
    alertRules: 'See your alert rules',
    alert: 'Open this alert',
    activity: 'See what changed',
  },

  // ---- the last card, in its two forms (REQ-005 AC4) ---------------------------------------------------
  offerTitle: 'Want a heads-up before anything surprises you?',
  offerLead:
    'These three alerts suit this account. They only tell you things — they never change your ads.',
  /** While the three rules are being written and the plan adopted. */
  enabling: 'Switching them on…',
  decline: 'No thanks — go to Home',
  /** REQ-005 AC4's other form: the presets are already on, so there is nothing to offer. */
  alreadyLink: 'See your alert rules',
  /**
   * w11's sub-line under "already watching", with the figure `FirstRun.rulesOn` carries (0.3.9, D-511) —
   * the owner's rules that are ON, which is the same count the gallery's limit line and the inbox's lead
   * sentence use (built-ins excluded, D-399). w11 goes on "…, including the one that spotted this month's
   * overspend"; the wire names no such rule, so the clause is left out rather than guessed.
   */
  alreadyCount: (n: number): string =>
    n === 1 ? '1 alert rule is on.' : `${n} alert rules are on.`,
  goHome: 'Go to Home',

  /** What the tap came to (w11's "After the one tap"). */
  doneEyebrow: 'All set',
  doneLead: 'Now watching your account',
  doneUnit: (n: number): string => (n === 1 ? 'alert on' : 'alerts on'),
  doneOpenAlerts: 'Open Alerts',

  // ---- the thin-data variant (REQ-005 AC5) --------------------------------------------------------------
  thinLead:
    "We'd rather wait than guess. A week of results is the least we need to say anything useful about your money.",
  thinComing: "Here's what you'll see",
  /**
   * w11's days row, which `FirstRun.daysOfData` (0.3.9) finally makes sayable. The dots are decoration over
   * the figure, never shape alone (§13 rule 2), and the long form is what a screen reader gets.
   */
  days: (days: number, of: number): string => `${days} of ${of} days`,
  daysLabel: (days: number, of: number): string => `${days} of ${of} days collected`,
  /** w11's wait sentence, with the figure `daysOfData` gives it. */
  thinWait: (left: number): string =>
    left <= 0
      ? 'Your first look will be waiting on Home very soon.'
      : `Your first look will be waiting on Home in about ${left} ${left === 1 ? 'day' : 'days'}.`,
  /** The same promise with no count, for an answer that carries no `daysOfData` (D-424's rule). */
  thinWaitNoCount: "Your first look will be waiting on Home once there's a week of results.",

  // ---- states ------------------------------------------------------------------------------------------
  loading: 'Getting your first look ready',
  errorTitle: "We couldn't open your first look",
  errorBody: 'Nothing is wrong with your account — we just could not fetch this one thing.',
  retry: 'Try again',
  enableFailed: "We couldn't switch those alerts on. Nothing has changed.",
} as const;

/** The Help menu and its one entry (REQ-005 AC1 — "re-openable from Help"). */
export const HELP_COPY = {
  trigger: 'Help',
  title: 'Help',
  firstRun: 'Show me around again',
  firstRunHint:
    'Your first look at this account — what you spent, what you got for it, and what to watch.',
} as const;
